import { useEffect, useState, useRef } from 'react';
import { FormHandles } from '@unform/core';
import { RiDeleteBinLine } from 'react-icons/ri';
import { MdQrCodeScanner } from 'react-icons/md';
import { Button, Flex, Icon, Stack, useToast } from '@chakra-ui/react';
import * as Yup from 'yup';

import api from '../../../services/api';
import InputSelect from '../../Inputs/Selects/InputSelect';
import Input from '../../Inputs/Input';
import getValidationErrors from '../../../utils/getValidationErrors';

import {
	Container,
	Content,
	FormView,
	FormAddAponta,
	ListApontamentos,
	ItemApontamento,
	ModalHeader,
	ModalFooter
} from './styles';

type FichaData = {
	banca: string;
	cliente: string;
	cod_emp: number;
	cod_ficha: number;
	cod_ped: string;
	cod_pro: number;
	dat_cad: string;
	descricao: string;
	imagem: string;
	nome_pro: string;
	proxima_op: string;
	quant_atual: number;
	quant_incial: number;
	status_des: string;
	usuario: string;
	apontamento: boolean;
}
interface ModalParams {
	id?: string;
	onClose: any;
	isVisible: boolean;
	apontamentos: FichaData[];
	handleAddAponta: (cod_ficha: number) => void;
	handleRemoveAponta: (cod_ficha: number) => void;
	setModalApontaScroll: (open: boolean) => void;
	handleCleanAponta: () => void;
	handleGetFichas: (data: any) => void;
	dataGetFichas: any;
}

const ModalApontamento: React.FC<ModalParams> = ({
	id = "modalApontamento",
	onClose,
	isVisible,
	apontamentos,
	handleAddAponta,
	handleRemoveAponta,
	setModalApontaScroll,
	handleCleanAponta,
	handleGetFichas,
	dataGetFichas
}) => {
	function handleOutSideClick(e: any) {
		if (e.target.id === id) {
			setVisible(false);
			setModalApontaScroll(false);
			setTimeout(() => {
				onClose();
			}, 300);
		}
	};

	const formRef = useRef<FormHandles>(null);
	const formInputRef = useRef<FormHandles>(null);

	const toast = useToast();

	const [visible, setVisible] = useState(isVisible);
	const [processos, setProcessos] = useState([]);
	const [bancas, setBancas] = useState([]);

	useEffect(() => {
		api.get('/portal/fichas/aponta')
			.then(response => {
				setProcessos(response.data.processos);
				setBancas(response.data.bancas);
			})
			.catch(err => console.log('Erro: ', err))
			.finally(() => {})
	}, []);

	function handleOnClose() {
		setVisible(false);
		setModalApontaScroll(false);
		setTimeout(() => {
			onClose();
		}, 200);
	};

	async function handleSubmit() {
		try {
			if (apontamentos.length === 0) {
				return;
			}
			
			const data = formRef.current?.getData();
			formRef.current?.setErrors({});

			const schema = Yup.object().shape({
				processo: Yup.string().required('Processo obtigatório.'),
				banca: Yup.string().required('Banca obtigatório.'),
			});

			await schema.validate(data, {
				abortEarly: false,
			});

			const body = {
				...data,
				fichas: apontamentos
			};

			// console.log('body: ', body);

			api.post('/portal/fichas/aponta', body)
				.then(response => {
					if (response.data.che === 'N') {
						toast({
							title: 'Apontamento',
							description: `${response.data.men}`,
							status: 'error',
							duration: 5000,
							position: 'top-right',
							isClosable: true,
							containerStyle: {
								marginTop: '20'
							}
						});
					};

					handleCleanAponta();
					handleOnClose();
					handleGetFichas(dataGetFichas);

					toast({
						title: 'Apontamento',
						description: `Apontamento feito com sucesso.`,
						status: 'success',
						duration: 5000,
						position: 'top-right',
						isClosable: true,
						containerStyle: {
							marginTop: '20'
						}
					});
				})
				.catch(err => {
					toast({
						title: 'Erro',
						description: `${err}`,
						status: 'error',
						duration: 5000,
						position: 'top-right',
						isClosable: true,
						containerStyle: {
							marginTop: '20'
						}
					});
				})
		} catch (err) {
			console.log('ERROR: ', err);
			if (err instanceof Yup.ValidationError) {
				const errors = getValidationErrors(err);
				formRef.current?.setErrors(errors);
				return;
			}
		}
	};

	return (
		<Container visible={visible} id="modalApontamento" onClick={handleOutSideClick}>
			<Content className={`${visible ? 'active' : ''}`}>
				<ModalHeader>
					<h1>Apontamento</h1>
				</ModalHeader>

				<FormView ref={formRef} onSubmit={() => {}}>
					<div className="input-content">
						<div className="input">
							<span>Processo Massa</span>
							<InputSelect
								name="processo"
								options={processos}
								placeholder="Processo"
								defaultValue=""
								containerStyle={{ padding: '4px 5px' }}
							/>
						</div>
					</div>

					<div className="input-content">
						<div className="input">
							<span>Banca</span>
							<InputSelect
								name="banca"
								options={bancas}
								placeholder="Banca"
								defaultValue=""
								containerStyle={{ padding: '4px 5px' }}
							/>
						</div>
					</div>
				</FormView>

				<Flex width="100%">
					<FormAddAponta ref={formInputRef}
						onSubmit={(e) => {
							handleAddAponta(Number(e.cod));
							formInputRef.current?.reset();
						}}
						style={{ flexDirection: 'row' }}
					>
						<Stack direction="row">
							<Input
								name="cod"
								placeholder="Código ficha"
								containerStyle={{ padding: '4px 10px' }}
							/>

							<Button type="submit" colorScheme="blue">Add</Button>
						</Stack>
					</FormAddAponta>

					<Flex width="100%" justify="flex-end">
						<Button p={0}>
							<Icon as={MdQrCodeScanner} fontSize={30} />
						</Button>
					</Flex>
				</Flex>

				<ListApontamentos>
					{apontamentos.map(aponta => {
						return (
							<ItemApontamento key={aponta.cod_ficha}>
								<Flex>
									<Button
										p={0}
										size="sm"
										ml="1"
										bg="transparent"
										_hover={{ bg: 'transparent' }}
										onClick={() => handleRemoveAponta(aponta.cod_ficha)}
									>
										<Icon as={RiDeleteBinLine} fontSize="25" color="red.500" />
									</Button>
								</Flex>

								<Flex justify="center" direction="column" pl={2}>
									<Flex align="center">
										{aponta.cod_ficha} - {aponta.nome_pro}
									</Flex>

									<Flex align="center" fontSize="15px">
										{aponta.status_des} - 
										qtd. Inicial: {aponta.quant_incial} - 
										qtd. Final: {aponta.quant_atual}
									</Flex>
								</Flex>
							</ItemApontamento>
						)
					})}
				</ListApontamentos>

				<ModalFooter>
					<Button
						m={2}
						w="100%"
						colorScheme="blue"
						disabled={!formRef.current?.getData}
						onClick={handleSubmit}
					>
						Confirmar
					</Button>

					<Button
						m={2}
						w="100%"
						bg="#c53030"
						colorScheme="red"
						onClick={() => {
							handleCleanAponta();
							handleOnClose();
						}}
					>
						Cancelar
					</Button>
				</ModalFooter>
			</Content>
		</Container>
	)
};

export default ModalApontamento;